import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { CustomSelect } from '@/components/ui/custom-select';
import {
  Users, TrendingUp, TrendingDown, Minus, AlertTriangle,
  Calendar, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';

export default function TeamBandwidth({ sprintId, compact = false, selectedProduct: externalProduct }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [products, setProducts] = useState([]);
  const [internalProduct, setInternalProduct] = useState(externalProduct || 'all');

  // Derive effective product: external prop wins, user can override via dropdown
  const selectedProduct = externalProduct !== undefined ? externalProduct : internalProduct;

  useEffect(() => {
    api.get('/products/').then(res => setProducts(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (sprintId) {
      let cancelled = false;
      const load = async () => {
        try {
          setLoading(true);
          const params = selectedProduct !== 'all' ? { product_id: selectedProduct } : {};
          const res = await api.get(`/observability/capacity/sprint/${sprintId}`, { params });
          if (!cancelled) setData(res.data);
        } catch (err) {
          console.error('Failed to load bandwidth:', err);
        } finally {
          if (!cancelled) setLoading(false);
        }
      };
      load();
      return () => { cancelled = true; };
    }
  }, [sprintId, selectedProduct]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'overloaded': return 'bg-red-500 dark:bg-red-500/80 text-white';
      case 'at_capacity': return 'bg-amber-500 dark:bg-amber-500/80 text-white';
      case 'healthy': return 'bg-green-500 dark:bg-green-500/80 text-white';
      case 'available': return 'bg-blue-500 dark:bg-blue-500/80 text-white';
      default: return 'bg-gray-500 dark:bg-gray-500/80 text-white';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'overloaded': return 'Overloaded';
      case 'at_capacity': return 'At Capacity';
      case 'healthy': return 'Healthy';
      case 'available': return 'Available';
      default: return status;
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing': return <TrendingUp className="w-3 h-3 text-green-500" />;
      case 'decreasing': return <TrendingDown className="w-3 h-3 text-red-500" />;
      default: return <Minus className="w-3 h-3 text-gray-400" />;
    }
  };

  const team_members = data?.team_members || [];
  const totals = data?.totals || {};
  const unassigned_points = data?.unassigned_points || 0;
  const overloadedCount = team_members.filter(m => m.status === 'overloaded').length;

  // Collapsed view — header bar with summary
  if (!expanded) {
    return (
      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 overflow-hidden">
        <div
          onClick={() => setExpanded(true)}
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#f9f9f9] dark:hover:bg-white/5 transition-colors"
          role="button"
          aria-expanded="false"
          tabIndex={0}
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#98139C]" />
            <span className="font-medium text-[#30302F] dark:text-white">Team Bandwidth</span>
          </div>
          <div className="flex items-center gap-3">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-[#98139C]" />
            ) : (
              <>
                <span className="text-sm font-semibold text-[#30302F] dark:text-white">
                  {totals.utilization_percent || 0}%
                </span>
                {overloadedCount > 0 && (
                  <Badge className="bg-red-500 text-white">
                    {overloadedCount} overloaded
                  </Badge>
                )}
              </>
            )}
            <ChevronDown className="w-4 h-4 text-[#30302F]/60 dark:text-white/60" />
          </div>
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 overflow-hidden">
      {/* Header */}
      <div
        className="p-4 border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(false)}
        role="button"
        aria-expanded="true"
        tabIndex={0}
      >
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[#98139C]" />
          <h3 className="font-semibold text-[#30302F] dark:text-white">Team Bandwidth</h3>
        </div>
        <div className="flex items-center gap-3">
          {/* Product filter dropdown — only when no external product prop */}
          {externalProduct === undefined && (
            <div onClick={(e) => e.stopPropagation()}>
              <CustomSelect
                value={selectedProduct}
                onChange={(e) => setInternalProduct(e.target.value)}
                options={[
                  { value: 'all', label: 'All Products' },
                  ...products.map(p => ({ value: p.id, label: p.name }))
                ]}
                aria-label="Filter bandwidth by product"
              />
            </div>
          )}
          <span className="text-sm text-[#30302F]/80 dark:text-white/80">
            {totals.assigned_points || 0} / {totals.total_capacity || 0} pts
          </span>
          <Badge className={totals.utilization_percent > 100 ? 'bg-red-500 dark:bg-red-500/80 text-white' : totals.utilization_percent > 80 ? 'bg-amber-500 dark:bg-amber-500/80 text-white' : 'bg-green-500 dark:bg-green-500/80 text-white'}>
            {totals.utilization_percent || 0}%
          </Badge>
          <ChevronUp className="w-4 h-4 text-[#30302F]/60 dark:text-white/60" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="w-5 h-5 animate-spin text-[#98139C]" />
        </div>
      ) : !data || data.error ? (
        <div className="p-4 text-sm text-[#30302F]/70 dark:text-white/70 text-center">
          No bandwidth data available
        </div>
      ) : (
        <>
          {/* Team Members */}
          <div className="divide-y divide-[#e3e3e3] dark:divide-white/5">
            {team_members.map((member, i) => (
              <div key={i} className="p-4 hover:bg-[#f9f9f9] dark:hover:bg-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#98139C] to-[#4F0077] flex items-center justify-center text-white text-sm font-bold">
                      {member.assignee?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-[#30302F] dark:text-white">{member.assignee}</p>
                      <p className="text-xs text-[#30302F]/70 dark:text-white/70">
                        {member.story_count} stories • {member.assigned_points} pts assigned
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(member.status)}>
                      {getStatusText(member.status)}
                    </Badge>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs text-[#30302F]/70 dark:text-white/70 mb-1">
                    <span>Capacity: {member.effective_capacity} pts</span>
                    <span>{member.utilization_percent}%</span>
                  </div>
                  <div className="h-2 bg-[#e3e3e3] dark:bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        member.utilization_percent > 120 ? 'bg-red-500 dark:bg-red-400' :
                        member.utilization_percent > 100 ? 'bg-amber-500 dark:bg-amber-400' :
                        member.utilization_percent > 80 ? 'bg-green-500 dark:bg-green-400' : 'bg-blue-500 dark:bg-blue-400'
                      }`}
                      style={{ width: `${Math.min(member.utilization_percent, 150)}%` }}
                      role="progressbar"
                      aria-valuenow={member.utilization_percent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                </div>

                {/* Breakdown */}
                <div className="mt-2 flex items-center gap-4 text-xs">
                  <span className="text-green-600 dark:text-green-400">
                    ✓ {member.completed_points} done
                  </span>
                  <span className="text-blue-600 dark:text-blue-400">
                    → {member.in_progress_points} in progress
                  </span>
                  <span className="text-[#30302F]/70 dark:text-white/70">
                    ○ {member.remaining_points} remaining
                  </span>
                </div>

                {/* Velocity */}
                {member.historical_velocity && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-[#30302F]/70 dark:text-white/70">
                    <span>Avg velocity: {member.historical_velocity.average} pts</span>
                    {getTrendIcon(member.historical_velocity.trend)}
                  </div>
                )}

                {/* Availability Warning */}
                {member.availability && !member.availability.available && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                    <Calendar className="w-3 h-3" />
                    <span>{member.availability.unavailable_days} days unavailable this sprint</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Unassigned Warning */}
          {unassigned_points > 0 && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">{unassigned_points} points unassigned</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
